/* Presentation only: the app retains its own offers, pricing and cart behaviour. */
if (!customElements.get('nc-product-columns')) {
  customElements.define('nc-product-columns', class extends HTMLElement {
    connectedCallback() {
      this.columns = [...this.querySelectorAll('.product__column-sticky')];
      if (!this.columns.length || this.classList.contains('product--no-media')) return;
      this.abort = new AbortController();
      this.header = document.querySelector('sticky-header');
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          if (!this.isConnected) return;
          if (window.innerWidth < 750) {
            this.columns.forEach((column) => column.style.removeProperty('--nc-sticky-top'));
            return;
          }
          const gap = 16;
          const headerHeight = this.header?.getBoundingClientRect().height || 0;
          const heights = this.columns.map((column) => column.getBoundingClientRect().height);
          this.columns.forEach((column, index) => {
            // Tall columns scroll through before their bottom rests in the viewport.
            // Native sticky positioning then stops at the shared product-section boundary.
            const top = Math.min(headerHeight + gap, window.innerHeight - heights[index] - gap);
            column.style.setProperty('--nc-sticky-top', `${Math.round(top)}px`);
          });
        });
      };
      this.observer = new ResizeObserver(this.schedule);
      this.columns.forEach((column) => this.observer.observe(column));
      if (this.header) this.observer.observe(this.header);
      window.addEventListener('resize', this.schedule, { signal: this.abort.signal });
      this.schedule();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.columns?.forEach((column) => column.style.removeProperty('--nc-sticky-top'));
    }
  });
}

if (!customElements.get('nc-multi-buy-style')) {
  customElements.define('nc-multi-buy-style', class extends HTMLElement {
    connectedCallback() {
      this.roots = new Map();
      this.attachStyles = () => {
        if (!this.isConnected) return;
        this.querySelectorAll('section-store--bundle-block').forEach((widget) => {
          const root = widget.shadowRoot;
          if (!root) return;
          if (!root.querySelector('link[data-nc-multi-buy]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = this.dataset.stylesheet;
            link.dataset.ncMultiBuy = '';
            root.append(link);
          }
          if (!this.roots.has(root)) {
            const observer = new MutationObserver(this.attachStyles);
            observer.observe(root, { childList: true });
            this.roots.set(root, observer);
          }
        });
      };
      this.observer = new MutationObserver(this.attachStyles);
      this.observer.observe(this, { childList: true, subtree: true });
      customElements.whenDefined('section-store--bundle-block').then(this.attachStyles);
      this.attachStyles();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.roots?.forEach((observer) => observer.disconnect());
    }
  });
}

if (!customElements.get('nc-hero-video')) {
  customElements.define('nc-hero-video', class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('button');
      if (!this.video || !this.button) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = false;
      this.visible = false;
      this.pageReady = document.readyState === 'complete';
      this.video.muted = true;
      this.sync = () => {
        this.button.textContent = this.video.paused ? 'Play video' : 'Pause video';
        this.button.setAttribute('aria-label', this.video.paused ? 'Play hero video' : 'Pause hero video');
      };
      this.play = () => {
        if (!this.video.dataset.loaded) {
          this.video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
          this.video.dataset.loaded = 'true';
          this.video.load();
        }
        return this.video.play().catch(() => this.sync());
      };
      this.update = () => {
        if (this.pageReady && this.visible && !document.hidden && !this.motion.matches && !this.userPaused) this.play();
        else this.video.pause();
      };
      window.addEventListener('load', () => { this.pageReady = true; this.update(); }, { ...options, once: true });
      this.video.addEventListener('playing', () => { this.dataset.played = ''; this.sync(); }, options);
      this.video.addEventListener('pause', this.sync, options);
      this.video.addEventListener('error', () => {
        delete this.dataset.played;
        this.button.hidden = true;
      }, options);
      this.button.addEventListener('click', () => {
        if (this.video.paused) { this.userPaused = false; this.play(); }
        else { this.userPaused = true; this.video.pause(); }
      }, options);
      this.motion.addEventListener('change', this.update, options);
      document.addEventListener('visibilitychange', this.update, options);
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.update();
      }, { threshold: 0.15 });
      this.observer.observe(this);
      this.button.hidden = false;
      this.sync();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.video?.pause();
    }
  });
}
